import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';

import moment from 'moment';
import {
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Notifications,
  NotificationsOff,
} from '@mui/icons-material';
import TimezoneSelect from '../../../../../../components/TimezoneSelect';

import {
  addLocation,
  updateLocation,
} from '../../../../../../redux/location/actions';
import { getBarnUsersForAssignmentString } from '../../../../../../utils/scheduleHelper';
import BarnUsers from '../../../../../../components/Autocompletes/BarnUsers';
import { getLocationIcon } from '../../../../../../components/LocationChip';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 400,
  bgcolor: 'background.paper',
  p: 4,
  overflow: 'auto',
  maxHeight: '95%',
};

const AddEditLocationModalComponent = ({
  open,
  onClose,
  location,
}) => {
  const dispatch = useDispatch();
  // Redux
  const isFetchingLocations = useSelector((state) => state.location.isFetchingLocations);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);

  const [addingLocationError, setAddingLocationError] = React.useState(null);

  // form fields
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState('');
  const [assignedPlanner, setAssignedPlanner] = React.useState('');
  const [planningTimeUtcHour, setPlanningTimeUtcHour] = React.useState(17);
  const [timezone, setTimezone] = React.useState('');
  const [validation, setValidation] = React.useState({ });

  React.useEffect(() => {
    setName((location && location.name) || '');
    setType((location && location.type) || 'Barn');
    const tz = (location && location.timezone) || Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(tz);
    setAssignedPlanner((location && location.assignedPlanner) || '')
    setPlanningTimeUtcHour((location
      && location.planningTimeUtcHour && moment().utc().set('hour', location.planningTimeUtcHour).tz(tz)
      .hour()) || 17) // convert back to local timezone hour
    setValidation({});
    setAddingLocationError(null);
  }, [location]);

  const validateFields = () => {
    const updatedValidation = { name: '' };
    let isValid = true;
    if (name.length < 2 || name.length > 100) {
      updatedValidation.name = 'Name is required';
      isValid = false;
    }
    setValidation(updatedValidation);
    return isValid;
  };

  const onSave = () => {
    if (!validateFields()) { return; }

    if (!location) {
      // add horse location
      dispatch(addLocation({
        name,
        type,
        timezone,
        assignedPlanner,
        planningTimeUtcHour: !planningTimeUtcHour
          ? null
          : moment().set('hour', planningTimeUtcHour).utc().hour(),
      })).then((newLocation) => {
        setAddingLocationError(false);
        onClose(newLocation);
      }).catch((error) => {
        setAddingLocationError(error.message || 'There was an error attempting to add the location. Please contact Questri support.');
        console.error(error);
      });
    } else {
      dispatch(updateLocation(location.id, {
        name,
        type,
        timezone,
        assignedPlanner,
        planningTimeUtcHour: !planningTimeUtcHour
          ? null
          : moment().set('hour', planningTimeUtcHour).utc().hour(),
      })).then((updatedLocation) => {
        setAddingLocationError(false);
        onClose(updatedLocation);
      }).catch((error) => {
        setAddingLocationError(error.message || 'There was an error attempting to update the location. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => onClose()}
      aria-labelledby="modal-add-location-title"
      aria-describedby="modal-add-location-description"
    >
      <Box sx={modalStyle}>
        <Typography
          id="modal-add-horse-title"
          variant="h6"
          component="h2"
          align="center"
          gutterBottom
          sx={{ mb: 2 }}
        >
          {location
            ? 'Edit'
            : 'Add'} Location
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              sx={{ width: '100%' }}
              required
              id="name-field"
              label="Name"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              disabled={isFetchingLocations}
              error={!!validation.name}
              helperText={validation.name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="location-type-select-label">Location Type</InputLabel>
              <Select
                sx={{ width: '100%' }}
                labelId="location-type-select-label"
                id="location-type-select"
                value={type}
                label="Location Type"
                disabled={isFetchingLocations}
                onChange={(e) => setType(e.target.value)}
                MenuProps={{ style: { maxHeight: 400 } }}
              >
                <MenuItem value="Barn">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ mr: 1 }}>{getLocationIcon('Barn')}</Box>
                    Barn
                  </Box>
                </MenuItem>
                <MenuItem value="Trainer">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ mr: 1 }}>{getLocationIcon('Trainer')}</Box>
                    Trainer
                  </Box>
                </MenuItem>
                <MenuItem value="Competition">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ mr: 1 }}>{getLocationIcon('Competition')}</Box>
                    Show
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TimezoneSelect
              label="Location Timezone"
              value={timezone}
              onChange={(e, value) => setTimezone(value)}
              disabled={!!location || isFetchingLocations}
              helperText={location
                ? 'Cannot be modified after creation'
                : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 4,
                mb: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 300 }}>Location Planning Settings</Typography>
              <Box sx={{ width: '65%' }}><Divider /></Box>
            </Box>
            <Typography
              sx={{
                fontWeight: 300,
                fontSize: 12,
              }}
            >
              Each location with active horses will need daily planning. Assign responsible
              user(s) and a daily reminder time to complete the next days plan.
            </Typography>
          </Grid>
          <Grid xs={12} md={6} item>
            <BarnUsers
              label="Assigned Planner(s)"
              id="planning-users-select-autocomplete"
              multiple
              value={getBarnUsersForAssignmentString(assignedPlanner, barnUsersMap)}
              onSelect={(e, newBarnUsers) => {
                // take selected barn users and map to assignment syntax @[NAME](AUTH0_USER_ID)
                const locationPlanners = newBarnUsers.map((bu) => (bu.id
                  ? `@[${bu.userFullName}](${bu.auth0UserId || bu.email})`
                  : `@[${bu.userFullName || bu}](${null})`)).join('');
                setAssignedPlanner(locationPlanners);
              }}
              limitTags={1}
            />
          </Grid>
          <Grid xs={10} md={5} item>
            <FormControl
              size="small"
              fullWidth
            >
              <InputLabel>Reminder Time</InputLabel>
              <Select
                value={planningTimeUtcHour}
                label="Reminder Time"
                onChange={(e) => setPlanningTimeUtcHour(e.target.value)}
                MenuProps={{ style: { maxHeight: 400 } }}
              >
                <MenuItem key={0} value={0}>12am</MenuItem>
                <MenuItem key={1} value={1}>1am</MenuItem>
                <MenuItem key={2} value={2}>2am</MenuItem>
                <MenuItem key={3} value={3}>3am</MenuItem>
                <MenuItem key={4} value={4}>4am</MenuItem>
                <MenuItem key={5} value={5}>5am</MenuItem>
                <MenuItem key={6} value={6}>6am</MenuItem>
                <MenuItem key={7} value={7}>7am</MenuItem>
                <MenuItem key={8} value={8}>8am</MenuItem>
                <MenuItem key={9} value={9}>9am</MenuItem>
                <MenuItem key={10} value={10}>10am</MenuItem>
                <MenuItem key={11} value={11}>11am</MenuItem>
                <MenuItem key={12} value={12}>12pm</MenuItem>
                <MenuItem key={13} value={13}>1pm</MenuItem>
                <MenuItem key={14} value={14}>2pm</MenuItem>
                <MenuItem key={15} value={15}>3pm</MenuItem>
                <MenuItem key={16} value={16}>4pm</MenuItem>
                <MenuItem key={17} value={17}>5pm</MenuItem>
                <MenuItem key={18} value={18}>6pm</MenuItem>
                <MenuItem key={19} value={19}>7pm</MenuItem>
                <MenuItem key={20} value={20}>8pm</MenuItem>
                <MenuItem key={21} value={21}>9pm</MenuItem>
                <MenuItem key={22} value={22}>10pm</MenuItem>
                <MenuItem key={23} value={23}>11pm</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={2} md={1} item>
            <Tooltip
              title={planningTimeUtcHour
                ? 'Notifications On: Click to turn off daily mobile notifications for planning at this location'
                : 'Notifications Off: Click to turn on and receive a daily mobile notification reminder to do planning at this location'}
            >
              <IconButton
                color="secondary"
                onClick={() => {
                  setPlanningTimeUtcHour(planningTimeUtcHour
                    ? null
                    : (location
                  && location.planningTimeUtcHour && moment().utc().set('hour', location.planningTimeUtcHour).tz((location && location.timezone) || Intl.DateTimeFormat().resolvedOptions().timeZone)
                      .hour()) || 17)
                }}
              >
                {planningTimeUtcHour
                  ? <Notifications />
                  : <NotificationsOff />}
              </IconButton>
            </Tooltip>
          </Grid>

          {
            addingLocationError && <Grid xs={12}><Typography variant="caption" color="error" gutterBottom>{addingLocationError}</Typography></Grid>
          }

          <Grid item xs={12} align="right">
            <Divider sx={{ mb: 2 }} />
            <Alert severity="warning" sx={{ mb: 1 }}>
              <div>Note: Once a location is created, the timezone cannot be modified.</div>
              {!location && <div>Please ensure this is correct before saving.</div>}
            </Alert>
            <Button onClick={() => onClose()} disabled={isFetchingLocations}>Close</Button>
            <LoadingButton onClick={onSave} disabled={isFetchingLocations} variant="contained" color="primary" loading={isFetchingLocations} loadingPosition="start" startIcon={<SaveIcon />}>Save</LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

AddEditLocationModalComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  location: PropTypes.object,
};

AddEditLocationModalComponent.defaultProps = {
  open: false,
  location: null,
};

export default (AddEditLocationModalComponent);
